import {Component, OnInit} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Store} from '@ngrx/store';
import {Cart} from '@spartacus/cart/base/root';
import {combineLatest, forkJoin, Observable, of} from 'rxjs';
import {filter, switchMap, take} from 'rxjs/operators';
import {CartDataService} from '../../../checkout/services/cart-data.service';
import {ConfigService, FeatureTogglesService} from '@dcp/configurator';
import {CardConfig, ConsignmentCardConfig} from '../../../model/dcp-config-service/card-config';
import {
  DelegoRapidPayThreeDSecureInfo,
  DelegoRapidPayTokenizePaymentResponse
} from '../../../model/delego-rapidpay/delego-rapidpay';
import {
  DelegoRapidPayThreeDSecureInfoWithDelegoToken
} from '../../../model/delego-rapidpay/delego-rapidpay-authenticate';
import {DelegoRapidPayAuthorization} from '../../../model/delego-rapidpay/delego-rapidpay-authorization';
import {DelegoRapidPayIFrame} from '../../../model/delego-rapidpay/delego-rapidpay-iframe';
import {ConsignmentMetaData} from '../../../model/payment/consignment-meta-data.model';
import {ConsignmentPaymentMetaData} from '../../../model/payment/consignment-payment-meta-data.model';
import {DcpActiveCartService} from '../../../services/dcp-active-cart.service';
import {CardPaymentSessionStorageService} from '../../services/card-payment-session-storage.service';
import {DcpCheckoutPaymentService} from '../../services/dcp-checkout-payment.service';
import {DelegoRapidpayService} from '../../services/delego-rapidpay/delego-rapidpay.service';
import {PaymentError} from '../../services/error-handling/payment-error.enum';
import * as delegoActions from '../../services/store/actions/delego.action';
import {DelegoPaymentState} from '../../services/store/payment-state';
import * as fromDelego from '../../services/store/reducers/delego.reducer';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';

@UntilDestroy()
@Component({
  selector: 'dcp-delego-silent-payment',
  templateUrl: './delego-silent-payment.component.html',
})
export class DelegoSilentPaymentComponent implements OnInit {

  delegoJwtToken: string;
  rapidPayHandler: DelegoRapidPayIFrame;
  delegoFutureContractFT: boolean;

  constructor(private store: Store,
              private activeCartService: DcpActiveCartService,
              private delegoRapidPayService: DelegoRapidpayService,
              private dcpCheckoutPaymentService: DcpCheckoutPaymentService,
              private configService: ConfigService,
              private cardPaymentHelperService: CardPaymentSessionStorageService,
              private cartDataService: CartDataService,
              private featureTogglesService: FeatureTogglesService,
  ) {
  }

  ngOnInit() {
    this.subcribeToFeatureToggles();
    this.subscribeToAuthorizationStart();
  }

  subscribeToAuthorizationStart() {
    this.store.select(fromDelego.getState).pipe(
      filter((state) => state.start),
      switchMap((state: DelegoPaymentState) => this.activeCartService.isActiveCartHasCardPayment().pipe(take(1))),
      untilDestroyed(this),
    ).subscribe((isCardPayment) => {
      if (isCardPayment) {
        this.store.dispatch(new delegoActions.AuthorizationInProcess());
        this.authorizationStart();
      } else {
        this.store.dispatch(new delegoActions.AuthorizationNotRequired());
      }
    });
  }

  authorizationStart() {
    combineLatest([
      this.activeCartService.getActiveCart(),
      this.delegoRapidPayService.getCcDelegoJWT(),
    ]).pipe(take(1), untilDestroyed(this))
      .subscribe(([cart, delegoJwtToken]) => {
          this.delegoJwtToken = delegoJwtToken.token;
          if (!this.delegoRapidPayService.isPaymentInfoSet(cart)) {
            this.store.dispatch(new delegoActions.ErrorOccurred(PaymentError.PAYMENT_INFO_NOT_SET));
            return;
          }
          const consignmentsMetaData = this.dcpCheckoutPaymentService.extractConsignmentsMetaData(cart);
          this.store.dispatch(new delegoActions.AuthorizationSetup({
            cartCode: cart.code,
            consignments: consignmentsMetaData
          }));

          const creditCardConfigs$: Observable<ConsignmentCardConfig>[] = this.getConsignmentCardConfigs(consignmentsMetaData);
          forkJoin(creditCardConfigs$).pipe(untilDestroyed(this)).subscribe((consignmentCardConfig: ConsignmentCardConfig[]) => {
              consignmentCardConfig.forEach(consignmentCardConfig => {
                this.store.dispatch(new delegoActions.SetMerchantIdSuccess({
                  consignmentCode: consignmentCardConfig.consignment.code,
                  merchantId: consignmentCardConfig.cardConfig.merchantId,
                }));
                this.performAuthentication(cart, consignmentCardConfig);
              });
            },
            () => {
              this.store.dispatch(new delegoActions.ErrorOccurred(PaymentError.COULD_NOT_GET_MERCHANT_ID));
            });
        },
        () => {
          this.store.dispatch(new delegoActions.ErrorOccurred(PaymentError.COULD_NOT_GET_DELEGO_TOKEN));
        });
  };

  performAuthentication(cart: Cart, consignmentCardConfig: ConsignmentCardConfig): void {
    const cardConfig: CardConfig = consignmentCardConfig.cardConfig;
    const consignment: ConsignmentPaymentMetaData = consignmentCardConfig.consignment;
    const threeDSRequired = !!cardConfig['3DSEnable'];
    this.rapidPayHandler = this.delegoRapidPayService.openRapidPay({
      url: this.configService.getString('delegoRapidPay.url'),
      token: this.delegoJwtToken,
      paymentRequest: {
        currencyCode: cart.totalPrice.currencyIso,
        total: cart.totalPrice.value,
      },
      element: '#delegoIFrame',
      sorg: cardConfig.merchantId,
      events: {
        onApplicationInitialized: () => {
          const tokenizePaymentResponse = this.delegoRapidPayService.convertPaymentDetailsToDelego(cart);

          if (!threeDSRequired) {
            const payload: any = {
              consignmentCode: consignment.code,
              authenticationNotRequired: true,
            };

            this.store.dispatch(new delegoActions.AuthenticationNotRequired(payload));
            this.authorizeCard(cart, consignment, cardConfig.merchantId);
            return;
          }

          let totalValue;
          try {
            totalValue = this.getTotal(cart, consignment);
          } catch (error) {
            this.store.dispatch(new delegoActions.ErrorOccurred(PaymentError.NON_ZERO_GROSS_VALUE_NO_CALCULATION_BREAKDOWN));
            return;
          }
          this.rapidPayHandler.authenticate({
            amount: {
              // this needs to be adjusted as soon as we provide more than 0 value transactions
              value: totalValue,
              currency: cart.totalPrice.currencyIso,
            },
            billingAddress: tokenizePaymentResponse.billingAddress,
            payment: tokenizePaymentResponse.payment,
            useVerifiableResponse: true,
          }).then((jwt: string) => {
            const authResponse = this.delegoRapidPayService.decodeJwtResponse<DelegoRapidPayThreeDSecureInfoWithDelegoToken>(jwt);
            this.handleAuthenticationResponse(consignment, authResponse, cart, cardConfig.merchantId);
          }).catch((err) => { // unexpected errors
            this.store.dispatch(new delegoActions.ErrorOccurred(PaymentError.RAPIDPAY_SDK_FAILURE));
          });
        },
        onFailure: (error) => {
          this.store.dispatch(new delegoActions.ErrorOccurred(PaymentError.RAPIDPAY_SDK_FAILURE));
        },
      },
    });
  }

  authorizeCard(cart: Cart, consignment: ConsignmentPaymentMetaData, merchantId: string, threeDSecure?: DelegoRapidPayThreeDSecureInfo) {
    const tokenizePaymentResponse = this.delegoRapidPayService.convertPaymentDetailsToDelego(cart);
    const amount = this.getTotal(cart, consignment);
    const delegoAuthorizationCacheJwt = this.cardPaymentHelperService.getAuthorizeJwt(consignment.code);
    if (this.isNeedToPerformAuthorization(amount, cart.totalPrice.currencyIso, delegoAuthorizationCacheJwt)) {
      this.performAuthorization(consignment, tokenizePaymentResponse, amount, cart.totalPrice.currencyIso, threeDSecure);
    } else {
      this.handleAuthorizationResponse(consignment.code, delegoAuthorizationCacheJwt);
    }
  }

  performAuthorization(consignment: ConsignmentPaymentMetaData,
                       tokenizePaymentResponse: DelegoRapidPayTokenizePaymentResponse,
                       amount: number,
                       currency: string,
                       threeDSecure?: DelegoRapidPayThreeDSecureInfo): void {
    this.rapidPayHandler.authorize({
      amount: {
        value: amount,
        currency,
      },
      payment: tokenizePaymentResponse.payment,
      address: tokenizePaymentResponse.billingAddress,
      useVerifiableResponse: true,
      preAuth: false,
      threeDSecure
    }).then((jwt: string) => {
      this.cardPaymentHelperService.setAuthorizeJwt(consignment.code, jwt);
      this.handleAuthorizationResponse(consignment.code, jwt);
    }).catch((err) => { // unexpected error
      this.store.dispatch(new delegoActions.ErrorOccurred(PaymentError.RAPIDPAY_SDK_FAILURE));
    });
  }

  handleAuthenticationResponse(consignment: ConsignmentPaymentMetaData,
                               authResponse: DelegoRapidPayThreeDSecureInfoWithDelegoToken,
                               cart: Cart,
                               merchantId: string): void {
    const payload = {
      consignmentCode: consignment.code,
      authenticationResult: authResponse,
    };
    if (this.delegoRapidPayService.isAuthenticationSuccessful(authResponse, cart.paymentInfo.cardType.code)) {
      this.authorizeCard(cart, consignment, merchantId, authResponse);
      this.store.dispatch(new delegoActions.AuthenticationSuccess(payload));
    } else {
      this.store.dispatch(new delegoActions.AuthenticationFailure(payload));
    }
  }

  handleAuthorizationResponse(consignmentCode: string, jwt: string) {
    const authorization = this.delegoRapidPayService.decodeJwtResponse<DelegoRapidPayAuthorization>(jwt);
    const payload = {
      consignmentCode,
      authorizationResult: authorization,
    };
    if (authorization.authorized) {
      this.store.dispatch(new delegoActions.SetAuthorizationSuccess(payload));
    } else {
      this.store.dispatch(new delegoActions.SetAuthorizationFailure(payload));
    }
  }

  getTotal(cart: Cart, consignment: ConsignmentPaymentMetaData): number {
    const priceTotalsBySalesArea = this.cartDataService.getPriceTotalsBySalesArea(cart);
    const price = priceTotalsBySalesArea[consignment.code].find(value => value.code === 'all').grossValue;

    if (this.delegoFutureContractFT && this.isAtLeast7DaysAfterContractStartDate(cart, consignment)) {
      return 1;
    }

    // return 1 instead of 0
    return Number(price) || 1;
  }

  isAtLeast7DaysAfterContractStartDate(cart: Cart, consignment: ConsignmentPaymentMetaData): boolean {
    const priceTotalsBySalesAreaTypeAll = JSON.parse(cart.data?.find((e) => e.type === 'priceTotalsBySalesArea')?.value)[consignment.code].find(e => e.code = 'all');
    const grossValue = Number.parseFloat(priceTotalsBySalesAreaTypeAll.grossValue);

    if (grossValue === 0) {
      // when grossValue is 0 don't need to adjust price
      return false;
    }

    if (grossValue > 1 && !priceTotalsBySalesAreaTypeAll.priceCalculationBreakdown) {
      throw PaymentError.NON_ZERO_GROSS_VALUE_NO_CALCULATION_BREAKDOWN;
    }
    const contractStartDate = new Date(priceTotalsBySalesAreaTypeAll.priceCalculationBreakdown[0].startDate);
    contractStartDate.setHours(0, 0, 0, 0);

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const oneDayInMs = 1000 * 60 * 60 * 24;
    return (contractStartDate.getTime() - currentDate.getTime()) > (7 * oneDayInMs);
  }

  /**
   *  We need to perform authorize call only when cached
   *  authorization amount is not the same as
   *  to current grossValue amount
   *  Method returns false when there is no cached authorization response.
   *
   * @param currency current grossValue currency
   * @param value current grossValue value
   * @param delegoAuthorizationCacheJwt cached authorize response
   */
  private isNeedToPerformAuthorization(value: number, currency: string, delegoAuthorizationCacheJwt: string): Boolean {
    if (delegoAuthorizationCacheJwt) {
      const decodedAuthorization: DelegoRapidPayAuthorization = this.delegoRapidPayService.decodeJwtResponse<DelegoRapidPayAuthorization>(delegoAuthorizationCacheJwt);
      const authorizedValue: number = decodedAuthorization?.authorizedAmount?.value;
      const authorizedCurrency: string = decodedAuthorization?.authorizedAmount?.currency;
      return this.isDifferentAuthorizedAmount(authorizedValue, value) || this.isDifferentAuthorizedCurrency(authorizedCurrency, currency);
    }
    return true;
  }

  private isDifferentAuthorizedAmount(authorizedValue: number, currentAmount: number): boolean {
    return !(authorizedValue === currentAmount);
  }

  private isDifferentAuthorizedCurrency(authorizedCurrency: string, currentCurrency: string): boolean {
    return !(authorizedCurrency === currentCurrency);
  }

  private getConsignmentCardConfigs(consignments: ConsignmentMetaData[]): Observable<ConsignmentCardConfig>[] {
    const consignmentsWithoutSavedConfigs = consignments.filter(c => this.isNeedToGetConsignmentCardConfig(c.code));
    const consignmentCardConfigs: Observable<ConsignmentCardConfig>[] = this.dcpCheckoutPaymentService.getConsignmentCardConfigs(consignmentsWithoutSavedConfigs);
    const savedConsignmentCardConfigs: Observable<ConsignmentCardConfig>[] = this.getSavedConsignmentCardConfigs(consignments);
    return consignmentCardConfigs.concat(savedConsignmentCardConfigs);
  }

  private getSavedConsignmentCardConfigs(consignments: ConsignmentPaymentMetaData[]): Observable<ConsignmentCardConfig>[] {
    return consignments
      .filter(consignment => this.isConsignmentCardConfigSaved(consignment.code))
      .map(consignment => of({
        consignment,
        cardConfig: {
          merchantId: this.cardPaymentHelperService.getMerchantId(consignment.code),
          '3DSEnable': this.cardPaymentHelperService.getThreeDSRequired(consignment.code)
        }
      }));
  }

  private isConsignmentCardConfigSaved(consignmentCode: string): boolean {
    return this.isThreeDSRequiredSaved(consignmentCode) && this.isMerchantIdSaved(consignmentCode);
  }

  private isNeedToGetConsignmentCardConfig(consignmentCode: string): boolean {
    return !this.isConsignmentCardConfigSaved(consignmentCode);
  }

  private isThreeDSRequiredSaved(consignmentCode: string): boolean {
    return this.cardPaymentHelperService.getThreeDSRequired(consignmentCode);
  }

  private isMerchantIdSaved(consignmentCode: string): boolean {
    return !!this.cardPaymentHelperService.getMerchantId(consignmentCode);
  }

  private subcribeToFeatureToggles() {
    this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.delegoFutureContractDateSetValue).pipe(untilDestroyed(this)).subscribe(enabled => {
      this.delegoFutureContractFT = enabled;
    });
  }
}
